import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  Textarea,
  useDisclosure,
  Heading,
  IconButton,
  HStack,
  useToast,
  Spinner,
  Center,
  Switch,
  Flex,
  Text,
  Badge,
  Divider,
  VStack,
  Code,
} from '@chakra-ui/react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/table';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/modal';
import {
  FormControl,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/form-control';
import { FaEdit, FaTrash, FaPlus, FaCode, FaExternalLinkAlt } from 'react-icons/fa';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isVersionsOpen, onOpen: onVersionsOpen, onClose: onVersionsClose } = useDisclosure();
  const { isOpen: isNewVersionOpen, onOpen: onNewVersionOpen, onClose: onNewVersionClose } = useDisclosure();
  const [selectedApp, setSelectedApp] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    repository_url: '',
    documentation_url: '',
    is_active: true,
  });
  const [versionFormData, setVersionFormData] = useState({
    version: '',
    description: '',
    release_notes: '',
    is_stable: true,
    is_deprecated: false,
    artifact_url: '',
    commit_sha: '',
  });
  const toast = useToast();

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/applications/', {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      } else {
        toast({
          title: 'Error fetching applications',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error fetching applications',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const fetchVersions = async (app) => {
    setVersionsLoading(true);
    setSelectedApp(app);
    onVersionsOpen();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/applications/${app.id}/versions/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setVersions(data);
      }
    } catch (error) {
      toast({
        title: 'Error fetching versions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleEdit = (app) => {
    setSelectedApp(app);
    setFormData({
      name: app.name || '',
      description: app.description || '',
      repository_url: app.repository_url || '',
      documentation_url: app.documentation_url || '',
      is_active: app.is_active !== false,
    });
    onOpen();
  };

  const handleAdd = () => {
    setSelectedApp(null);
    setFormData({
      name: '',
      description: '',
      repository_url: '',
      documentation_url: '',
      is_active: true,
    });
    onOpen();
  };

  const handleDelete = async (app) => {
    if (!window.confirm(`Are you sure you want to delete "${app.name}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(`/api/applications/${app.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
      });
      if (response.ok) {
        toast({
          title: 'Application deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchApplications();
      } else {
        toast({
          title: 'Error deleting application',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error deleting application',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const url = selectedApp
        ? `/api/applications/${selectedApp.id}/`
        : '/api/applications/';
      const method = selectedApp ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: `Application ${selectedApp ? 'updated' : 'created'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
        setSelectedApp(null);
        fetchApplications();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error saving application',
          description: JSON.stringify(errorData),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error saving application',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddVersion = () => {
    setVersionFormData({
      version: '',
      description: '',
      release_notes: '',
      is_stable: true,
      is_deprecated: false,
      artifact_url: '',
      commit_sha: '',
    });
    onNewVersionOpen();
  };

  const handleSaveVersion = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      
      const response = await fetch(`/api/applications/${selectedApp.id}/versions/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify(versionFormData),
      });

      if (response.ok) {
        toast({
          title: 'Version created',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onNewVersionClose();
        // Refresh versions list
        fetchVersions(selectedApp);
        fetchApplications();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error creating version',
          description: JSON.stringify(errorData),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error creating version',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleVersionInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVersionFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="cyan.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">Applications</Heading>
          <Text color="gray.500" mt={1}>Manage registered applications and versions</Text>
        </Box>
        <Button
          leftIcon={<FaPlus />}
          bgGradient="linear(to-r, cyan.500, cyan.600)"
          color="white"
          _hover={{
            bgGradient: "linear(to-r, cyan.600, cyan.700)",
            transform: "translateY(-2px)",
            boxShadow: "lg",
          }}
          onClick={handleAdd}
          borderRadius="lg"
          px={6}
        >
          Add Application
        </Button>
      </Flex>

      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.100"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.600" py={4}>Name</Th>
              <Th color="gray.600">Description</Th>
              <Th color="gray.600">Versions</Th>
              <Th color="gray.600">Latest</Th>
              <Th color="gray.600">Status</Th>
              <Th color="gray.600" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {applications.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                  No applications found. Click "Add Application" to create one.
                </Td>
              </Tr>
            ) : (
              applications.map((app) => (
                <Tr
                  key={app.id}
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.2s"
                >
                  <Td fontWeight="medium" color="gray.800">{app.name}</Td>
                  <Td color="gray.600" maxW="300px" isTruncated>
                    {app.description?.substring(0, 50)}{app.description?.length > 50 ? '...' : ''}
                  </Td>
                  <Td>
                    <Badge colorScheme="cyan" borderRadius="full" px={3} py={1}>
                      {app.version_count || 0} versions
                    </Badge>
                  </Td>
                  <Td>
                    {app.latest_version ? (
                      <Code colorScheme="green" fontSize="sm">
                        v{app.latest_version.version}
                      </Code>
                    ) : (
                      <Text color="gray.400" fontSize="sm">-</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={app.is_active ? 'green' : 'red'}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {app.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      <IconButton
                        aria-label="View versions"
                        icon={<FaCode />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => fetchVersions(app)}
                      />
                      <IconButton
                        aria-label="Edit application"
                        icon={<FaEdit />}
                        size="sm"
                        variant="ghost"
                        colorScheme="cyan"
                        onClick={() => handleEdit(app)}
                      />
                      <IconButton
                        aria-label="Delete application"
                        icon={<FaTrash />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(app)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Application Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            {selectedApp ? 'Edit' : 'Add New'} Application
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontWeight="medium">Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  placeholder="e.g., my-application"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Description</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  rows={3}
                  placeholder="A brief description of the application"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Repository URL</FormLabel>
                <Input
                  name="repository_url"
                  value={formData.repository_url}
                  onChange={handleInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  placeholder="https://github.com/org/repo"
                />
                <FormHelperText>Git repository URL</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Documentation URL</FormLabel>
                <Input
                  name="documentation_url"
                  value={formData.documentation_url}
                  onChange={handleInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  placeholder="https://docs.example.com"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel fontWeight="medium" mb={0}>Active</FormLabel>
                <Switch
                  name="is_active"
                  isChecked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  colorScheme="cyan"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button
              bgGradient="linear(to-r, cyan.500, cyan.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, cyan.600, cyan.700)",
              }}
              onClick={handleSave}
            >
              {selectedApp ? 'Save Changes' : 'Create Application'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Versions Modal */}
      <Modal isOpen={isVersionsOpen} onClose={onVersionsClose} size="4xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontSize="lg" fontWeight="bold">{selectedApp?.name}</Text>
                <Text fontSize="sm" color="gray.500">Version History</Text>
              </Box>
              <Button
                leftIcon={<FaPlus />}
                colorScheme="cyan"
                size="sm"
                onClick={handleAddVersion}
              >
                Add Version
              </Button>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {versionsLoading ? (
              <Center py={8}>
                <Spinner size="lg" color="cyan.500" />
              </Center>
            ) : versions.length === 0 ? (
              <Center py={8}>
                <Text color="gray.500">No versions registered yet.</Text>
              </Center>
            ) : (
              <Table variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Version</Th>
                    <Th>Description</Th>
                    <Th>Status</Th>
                    <Th>Commit</Th>
                    <Th>Created</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {versions.map((version) => (
                    <Tr key={version.id} _hover={{ bg: "gray.50" }}>
                      <Td>
                        <Code colorScheme="cyan" fontSize="sm">v{version.version}</Code>
                      </Td>
                      <Td maxW="200px" isTruncated>{version.description || '-'}</Td>
                      <Td>
                        <HStack spacing={1}>
                          {version.is_stable && (
                            <Badge colorScheme="green" size="sm">Stable</Badge>
                          )}
                          {version.is_deprecated && (
                            <Badge colorScheme="red" size="sm">Deprecated</Badge>
                          )}
                          {!version.is_stable && !version.is_deprecated && (
                            <Badge colorScheme="yellow" size="sm">Pre-release</Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td>
                        {version.commit_sha ? (
                          <Code fontSize="xs">{version.commit_sha.substring(0, 7)}</Code>
                        ) : '-'}
                      </Td>
                      <Td fontSize="sm" color="gray.600">
                        {new Date(version.created_at).toLocaleDateString()}
                      </Td>
                      <Td>
                        {version.artifact_url && (
                          <IconButton
                            aria-label="View artifact"
                            icon={<FaExternalLinkAlt />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            as="a"
                            href={version.artifact_url}
                            target="_blank"
                          />
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </ModalBody>
          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" onClick={onVersionsClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New Version Modal */}
      <Modal isOpen={isNewVersionOpen} onClose={onNewVersionClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            Add New Version
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontWeight="medium">Version</FormLabel>
                <Input
                  name="version"
                  value={versionFormData.version}
                  onChange={handleVersionInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  placeholder="e.g., 1.0.0"
                />
                <FormHelperText>Semantic version (e.g., 1.0.0, 2.1.3-beta)</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Description</FormLabel>
                <Input
                  name="description"
                  value={versionFormData.description}
                  onChange={handleVersionInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  placeholder="Brief description of this version"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Release Notes</FormLabel>
                <Textarea
                  name="release_notes"
                  value={versionFormData.release_notes}
                  onChange={handleVersionInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  rows={4}
                  placeholder="Changelog or release notes..."
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Artifact URL</FormLabel>
                <Input
                  name="artifact_url"
                  value={versionFormData.artifact_url}
                  onChange={handleVersionInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  placeholder="https://artifacts.example.com/build.zip"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Commit SHA</FormLabel>
                <Input
                  name="commit_sha"
                  value={versionFormData.commit_sha}
                  onChange={handleVersionInputChange}
                  borderRadius="lg"
                  focusBorderColor="cyan.500"
                  placeholder="abc123def456..."
                  maxLength={40}
                />
              </FormControl>
              <Divider />
              <HStack spacing={8}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontWeight="medium" mb={0}>Stable Release</FormLabel>
                  <Switch
                    name="is_stable"
                    isChecked={versionFormData.is_stable}
                    onChange={(e) => setVersionFormData({ ...versionFormData, is_stable: e.target.checked })}
                    colorScheme="green"
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontWeight="medium" mb={0}>Deprecated</FormLabel>
                  <Switch
                    name="is_deprecated"
                    isChecked={versionFormData.is_deprecated}
                    onChange={(e) => setVersionFormData({ ...versionFormData, is_deprecated: e.target.checked })}
                    colorScheme="red"
                  />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onNewVersionClose}>Cancel</Button>
            <Button
              bgGradient="linear(to-r, cyan.500, cyan.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, cyan.600, cyan.700)",
              }}
              onClick={handleSaveVersion}
            >
              Create Version
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Applications;
